import * as Sentry from '@sentry/react';
import type { PropsWithChildren } from 'react';
import type { BrowserOptions } from '@sentry/react';
import { useTranslation } from 'react-i18next';

const REDACTED = '[REDACTED]';
const KEY_DENYLIST = [
  /api[_-]?key/i,
  /auth/i,
  /bearer/i,
  /cookie/i,
  /email/i,
  /password/i,
  /prompt/i,
  /secret/i,
  /session/i,
  /signature/i,
  /token/i,
];

export interface WebSentryConfigInput {
  apiBase?: string | undefined;
  dsn?: string | undefined;
  enableOptionalTelemetry: boolean;
  environment: string;
  origin: string;
  release?: string | undefined;
}

function shouldRedactKey(key: string): boolean {
  return KEY_DENYLIST.some((pattern) => pattern.test(key));
}

function scrubText(value: string): string {
  return value
    .replace(/ab_(?:live|test)_[a-z0-9_]+/gi, REDACTED)
    .replace(/(?:sk|rk|pk)_(?:live|test)_[a-z0-9_]+/gi, REDACTED)
    .replace(/\bBearer\s+[a-z0-9._-]+\b/gi, REDACTED);
}

function scrubValue(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) {
    return value;
  }

  if (typeof value === 'string') {
    return scrubText(value);
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => scrubValue(entry, depth + 1));
  }

  if (typeof value !== 'object' || depth >= 5) {
    return String(value);
  }

  const record = value as Record<string, unknown>;

  return Object.fromEntries(
    Object.entries(record).map(([key, entry]) => {
      if (shouldRedactKey(key)) {
        return [key, REDACTED] as const;
      }

      if (key === 'url' && typeof entry === 'string') {
        return [key, scrubUrl(entry)] as const;
      }

      return [key, scrubValue(entry, depth + 1)] as const;
    }),
  );
}

export function scrubUrl(value: string): string {
  try {
    const url = new URL(value, globalThis.location?.origin);

    for (const [key] of url.searchParams) {
      if (shouldRedactKey(key)) {
        url.searchParams.set(key, REDACTED);
      }
    }

    return url.toString();
  } catch {
    return scrubText(value);
  }
}

export function createTracePropagationTargets({
  apiBase,
  origin,
}: Pick<WebSentryConfigInput, 'apiBase' | 'origin'>): Array<string | RegExp> {
  const targets: Array<string | RegExp> = [origin];

  if (apiBase) {
    targets.push(new URL(apiBase, origin).origin);
  }

  return Array.from(new Set(targets));
}

export function scrubBrowserEvent<T extends object>(event: T): T {
  const next = structuredClone(event) as T & {
    message?: unknown;
    data?: unknown;
    request?: Record<string, unknown>;
    extra?: Record<string, unknown>;
    contexts?: Record<string, unknown>;
  };

  if (typeof next.message === 'string') {
    next.message = scrubText(next.message);
  }

  if (next.data !== undefined) {
    next.data = scrubValue(next.data);
  }

  if (next.request && typeof next.request === 'object') {
    const request = next.request;

    if (typeof request.url === 'string') {
      request.url = scrubUrl(request.url);
    }

    if (request.data !== undefined) {
      request.data = scrubValue(request.data);
    }
  }

  if (next.extra && typeof next.extra === 'object' && !Array.isArray(next.extra)) {
    next.extra = scrubValue(next.extra) as Record<string, unknown>;
  }

  if (next.contexts && typeof next.contexts === 'object' && !Array.isArray(next.contexts)) {
    next.contexts = scrubValue(next.contexts) as Record<string, unknown>;
  }

  return next as T;
}

export function getWebSentryConfig(input: WebSentryConfigInput) {
  const beforeSend: NonNullable<BrowserOptions['beforeSend']> = (event) =>
    scrubBrowserEvent(event) as typeof event;
  const beforeSendTransaction: NonNullable<BrowserOptions['beforeSendTransaction']> = (event) =>
    scrubBrowserEvent(event) as typeof event;
  const beforeBreadcrumb: NonNullable<BrowserOptions['beforeBreadcrumb']> = (breadcrumb) =>
    scrubBrowserEvent(breadcrumb) as typeof breadcrumb;

  return {
    dsn: input.dsn,
    enabled: Boolean(input.dsn),
    release: input.release,
    environment: input.environment,
    sendDefaultPii: false,
    integrations: input.enableOptionalTelemetry ? [Sentry.browserTracingIntegration()] : [],
    tracesSampleRate: input.enableOptionalTelemetry ? 0.1 : 0,
    tracePropagationTargets: createTracePropagationTargets(input),
    beforeSend,
    beforeSendTransaction,
    beforeBreadcrumb,
  };
}

let sentryInitialized = false;

export function initializeWebSentry(config: ReturnType<typeof getWebSentryConfig>): void {
  if (sentryInitialized || !config.dsn) {
    return;
  }

  Sentry.init(config);
  sentryInitialized = true;
}

export function enableSentryPerformance(): void {
  if (!sentryInitialized) return;
  const client = Sentry.getClient();
  if (!client) return;
  const options = client.getOptions();
  const alreadyHasTracing = options.integrations.some((i) => i.name === 'BrowserTracing');
  if (!alreadyHasTracing) {
    client.addIntegration(Sentry.browserTracingIntegration());
  }
  options.tracesSampleRate = 0.1;
}

export function disableSentryPerformance(): void {
  if (!sentryInitialized) return;
  const client = Sentry.getClient();
  if (!client) return;
  const options = client.getOptions();
  options.tracesSampleRate = 0;
  options.integrations = options.integrations.filter((i) => i.name !== 'BrowserTracing');
}

function ErrorFallback({ resetError }: { resetError(): void }) {
  const { t } = useTranslation('common');

  return (
    <main className="min-h-screen bg-[#141414] px-8 py-16 text-[#eeeeff]">
      <div className="mx-auto flex max-w-xl flex-col gap-4">
        <h1 className="text-3xl font-semibold">{t('observability.errorBoundary.title')}</h1>
        <p className="text-sm text-[#b8b8d8]">{t('observability.errorBoundary.body')}</p>
        <button
          className="w-fit rounded-md border border-[rgba(255,255,255,0.12)] px-4 py-2 text-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
          onClick={resetError}
          type="button"
        >
          {t('observability.errorBoundary.retry')}
        </button>
      </div>
    </main>
  );
}

export function SentryAppBoundary({ children }: PropsWithChildren) {
  return (
    <Sentry.ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
      {children}
    </Sentry.ErrorBoundary>
  );
}
